/**
 * Formato de colones: FUENTE ÚNICA DE VERDAD.
 *
 * ESPEJO EXACTO de perronstore-app/src/app/shared/utils/currency.util.ts.
 * Cualquier cambio se aplica en LOS DOS repos, con los mismos tests. Si divergen,
 * el mismo importe vuelve a pintarse distinto en la tienda y en el backoffice, que
 * es el bug que este fichero existe para cerrar.
 *
 * ---
 *
 * **Por qué el separador se fija a mano y no con `Intl`/`toLocaleString`.**
 *
 * El CLDR cambió el separador de miles de `es-CR` de punto a espacio estrecho
 * (U+202F). El mismo importe pasó a pintarse `₡24 000` o `₡24.000` según la
 * versión de ICU del navegador, y cambiaba solo el día que el usuario
 * actualizaba Chrome. En producción (08/08/2026) llegaron a convivir las dos
 * formas en la misma sesión: `₡24 000` en las tarjetas del listado y `₡30.885`
 * en la línea de factura.
 *
 * Hay un segundo motivo, propio de perronstore-web: **renderiza en servidor**.
 * Todo formateo dependiente de Intl/CLDR puede divergir entre el ICU de Node y el
 * del navegador, y un importe que sale de una forma en el HTML del servidor y de
 * otra al hidratar es un hydration mismatch en una cifra que el cliente está
 * leyendo. Fijado a mano, la salida es idéntica en servidor y en cliente.
 *
 * Formato canónico, único en los dos repos: punto de miles, sin decimales,
 * prefijo `₡`, colones enteros. `₡12.000`, `₡1.234.567`, `₡0`, `-₡1.234`.
 *
 * Ver docs/specs/moneda-formato-crc-plan.md.
 */

/** Inserta el punto de miles en la parte entera ya en positivo. Sin `Intl`. */
function withThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Importe en colones enteros: `12000` → `'₡12.000'`.
 *
 * `null`, `undefined` y `NaN` devuelven **cadena vacía**, no `'₡0'`: sin precio
 * publicado no es lo mismo que gratis, y un producto sin precio que anunciara
 * `₡0` sería una regresión que cuesta dinero. Quien quiera el cero lo pasa:
 * `formatCrc(0) === '₡0'`.
 */
export function formatCrc(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '';
  const rounded = Math.round(Math.abs(value));
  // `Math.round(-0.4)` es `-0`, y `-0 < 0` es false: el signo se decide sobre el
  // valor redondeado, no sobre el original, para que `-0.4` no salga como `-₡0`.
  const sign = rounded !== 0 && value < 0 ? '-' : '';
  return `${sign}₡${withThousands(String(rounded))}`;
}

/**
 * Entero con separador de miles y SIN símbolo: `12000` → `'12.000'`.
 *
 * Dos usos, y solo dos: contadores que no son dinero (jugadores, miembros,
 * unidades) y huecos de copy que ya traen el `₡` escrito en la plantilla del
 * texto (`'…desde ₡{minSubtotal}'`). En el segundo caso usar `formatCrc` daría
 * `₡₡20.000`.
 */
export function formatInteger(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '';
  const rounded = Math.round(Math.abs(value));
  const sign = rounded !== 0 && value < 0 ? '-' : '';
  return `${sign}${withThousands(String(rounded))}`;
}

/**
 * Inverso de `formatCrc`: `'₡1.234.567'` → `1234567`. Entero SIEMPRE.
 *
 * Descarta TODO separador —`₡`, espacios (incluidos U+202F y U+00A0), puntos y
 * comas— y se queda con los dígitos y el signo. **No intenta adivinar
 * decimales**: en este dominio no existen (importe = entero en colones, por
 * contrato con la API). Por eso `parseCrc('1.234,50')` devuelve `123450` y está
 * bien: ese input no puede llegar. Los únicos campos con céntimos del sistema
 * son los de `pricing-card` del backoffice, que quedan fuera por diseño y no
 * usan esta función.
 *
 * Sin consumidor productivo hoy: la única directiva que enmascaraba importes en el
 * backoffice (services/separator.directive.ts) estaba muerta y se borró. Caso de uso
 * previsto: leer un importe que el usuario ha tecleado, o un string ya formateado en
 * CRC, y devolver el entero que viaja a la API. NO la uses para normalizar payload del
 * backend — para eso está toAmount(), que interpreta el punto como decimal.
 */
export function parseCrc(text: string | null | undefined): number {
  if (text == null) return 0;
  const raw = String(text);
  const negative = raw.trimStart().startsWith('-');
  const digits = raw.replace(/\D/g, '');
  if (!digits) return 0;
  const value = Number(digits);
  if (!Number.isFinite(value)) return 0;
  return negative ? -value : value;
}
